import { useMemo } from 'react'

import { DayOfWeek } from '@tribeplatform/gql-client/types'

import { dayjs } from '../../common/lib/dayjs.js'

/**
 * Maps dayjs day index (0-6) to DayOfWeek enum
 * dayjs uses: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
 */
const DAY_INDEX_TO_DAY_OF_WEEK: Record<number, DayOfWeek> = {
  0: DayOfWeek.SUNDAY,
  1: DayOfWeek.MONDAY,
  2: DayOfWeek.TUESDAY,
  3: DayOfWeek.WEDNESDAY,
  4: DayOfWeek.THURSDAY,
  5: DayOfWeek.FRIDAY,
  6: DayOfWeek.SATURDAY,
}

const DAY_OF_WEEK_TO_DAY_INDEX: Record<DayOfWeek, number> = {
  [DayOfWeek.SUNDAY]: 0,
  [DayOfWeek.MONDAY]: 1,
  [DayOfWeek.TUESDAY]: 2,
  [DayOfWeek.WEDNESDAY]: 3,
  [DayOfWeek.THURSDAY]: 4,
  [DayOfWeek.FRIDAY]: 5,
  [DayOfWeek.SATURDAY]: 6,
}

/**
 * Ordered list of days for consistent sorting (Monday first)
 */
export const DAYS_ORDER: DayOfWeek[] = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
]

export const getDayOfWeekFromDate = (date: Date | string): DayOfWeek => {
  const dayIndex = dayjs(date).day()
  return DAY_INDEX_TO_DAY_OF_WEEK[dayIndex]
}

/**
 * Sort days in weekday order (Monday to Sunday)
 */
export const sortDaysByWeekOrder = (days: DayOfWeek[]): DayOfWeek[] => {
  return [...days].sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b))
}

export const getDayOfWeekText = (dayOfWeek: DayOfWeek) => {
  return dayjs().weekday(DAY_OF_WEEK_TO_DAY_INDEX[dayOfWeek]).format('dddd')
}

export const useDaysOfWeekItems = () => {
  return useMemo(
    () =>
      DAYS_ORDER.map((day, index) => ({
        value: day,
        text: dayjs()
          .weekday(index + 1)
          .format('ddd'),
        readableText: dayjs()
          .weekday(index + 1)
          .format('dddd'),
      })),
    [],
  )
}
